import { Adapter } from "@/core/models/adapter";
import { ADMIN, NO_MODULE_TYPE, PSEUDO_ANONYMOUS } from "@/core/models/usersAndPermissions";

export class User {
  constructor() {
    this.id = null;
    this.types = null;
    this.email = null;
    this.firstName = null;
    this.lastName = null;
    // Only used for user management (get by id, create, update)
    this.accountId = null;
    this.timOrganizationId = null;
    this.rpsOrganizationId = null;
    this.status = null;
    this.forceConnectionBySso = false;
    // Only used for (current) authenticated user
    this.currentOrganization = null; // always null for CX admins & superadmins
    this.modules = null;
    this.frontPermissions = [];
    this.apiToken = null;
  }

  getCurrentOrganization() {
    return this.currentOrganization;
  }

  getCurrentModule() {
    return this.currentOrganization?.moduleType;
  }

  setCurrentOrganization(currentOrganization) {
    if (!currentOrganization) {
      return;
    }

    this.currentOrganization = {
      id: currentOrganization.id,
      commonName: currentOrganization.commonName,
      moduleType: currentOrganization.moduleType,
      account: {
        id: currentOrganization.account.id,
        name: currentOrganization.account.name,
        companyCountry: currentOrganization.account.companyCountry,
        language: currentOrganization.account.language,
        currency: currentOrganization.account.currency,
        emailDPO: currentOrganization.account.emailDPO,
        mainColor: currentOrganization.account.mainColor,
        logo: currentOrganization.account.logo,
        favicon: currentOrganization.account.favicon,
        isBuyerAndWorkshopsSupervisor: currentOrganization.account.isBuyerAndWorkshopsSupervisor,
        isRetailer: currentOrganization.account.isRetailer,
      },
    };
  }

  getCurrentType() {
    return this.modules?.[this.getCurrentModule()];
  }

  isPseudoAnonymous() {
    if (!this.types) return false;
    return this.types.includes(PSEUDO_ANONYMOUS);
  }

  isAdmin() {
    if (!this.types) return false;
    return this.types.includes(ADMIN);
  }

  hasPermission(permission) {
    return this.frontPermissions[this.getCurrentModule() ?? NO_MODULE_TYPE]?.includes(permission);
  }
}

export class UserAdapter extends Adapter {
  static adapt(userApi) {
    const user = new User();
    user.id = userApi?.id;
    user.types = userApi?.types ?? [];
    user.email = userApi?.email;
    user.firstName = userApi?.firstname;
    user.lastName = userApi?.lastname;
    user.accountId = userApi?.accountId;
    user.timOrganizationId = userApi?.timOrganizationId;
    user.rpsOrganizationId = userApi?.rpsOrganizationId;
    user.status = userApi?.status;
    user.forceConnectionBySso = userApi?.forceConnectionBySso;

    return user;
  }

  static adaptAuthenticatedUser(userApi) {
    const user = new User();
    user.id = userApi?.id;
    user.types = userApi?.types ?? [];
    user.email = userApi?.email;
    user.firstName = userApi?.firstname;
    user.lastName = userApi?.lastname;
    user.setCurrentOrganization(userApi?.currentOrganization);
    user.modules = userApi?.modules;
    user.frontPermissions = userApi?.frontPermissions;
    user.apiToken = userApi?.apiToken;

    return user;
  }

  /**
   * @param {User} user
   */
  static serializeAuthenticatedUser(user) {
    const userDictionary = {
      id: user?.id,
      types: user?.types,
      email: user?.email,
      firstname: user?.firstName,
      lastname: user?.lastName,
      currentOrganization: user?.currentOrganization,
      modules: user?.modules,
      frontPermissions: user?.frontPermissions,
      apiToken: user?.apiToken,
    };

    return JSON.stringify(userDictionary);
  }
}
